import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { Alert } from "@mui/material";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface Datasets {
  label: string;
  data: number[];
  backgroundColor: string[];
  borderColor: string[];
  borderWidth: number;
}
interface Data {
  labels: string[];
  datasets: Datasets[];
}
export interface PieChartProps {
  title: string;
  data?: Data;
}


export default function PieChart({title, data}: PieChartProps) {
  if (!data) return <Alert severity="error">Something went wrong</Alert>

  return (
    <Pie
      height={250}
      data={data}
      options={{
        responsive: false,
        plugins: {
          tooltip: {
            enabled: true,
          },
          title: {
            display: true,
            text: title,
            padding: {
              bottom: 10
            }
          },
          legend: {
            onClick: () => null,
            position: 'bottom',
            labels: {
              boxWidth: 4,
            },
            fullSize: true,
          }
        }
      }}
      plugins={[
        // add leader lines
        {
          id: 'leader_lines',
          afterDraw: (chart: any) => {
            const ctx = chart.ctx;
            ctx.save();
            ctx.font = "12px 'Averta Std CY'";
            const leftLabelCoordinates: any[] = [];
            const rightLabelCoordinates: any[] = [];
            const chartCenterPoint = {
              x:
                (chart.chartArea.right - chart.chartArea.left) / 2 +
                chart.chartArea.left,
              y:
                (chart.chartArea.bottom - chart.chartArea.top) / 2 +
                chart.chartArea.top
            };
            chart.config.data.labels.forEach((label: any, i: any) => {
              const meta = chart.getDatasetMeta(0);
              const arc = meta.data[i];
              const dataset = chart.config.data.datasets[0];

              // Prepare data to draw
              // important point 1
              const centerPoint = arc.getCenterPoint();
              let color = '#7A7A7A';
              let labelColor = '#7A7A7A';
              if (dataset.polyline && dataset.polyline.color) {
                color = dataset.polyline.color;
              }
      
              if (dataset.polyline && dataset.polyline.labelColor) {
                labelColor = dataset.polyline.labelColor;
              }
      
              const angle = Math.atan2(
                centerPoint.y - chartCenterPoint.y,
                centerPoint.x - chartCenterPoint.x
              );
              // important point 2, this point overlapsed with existed points
              // so we will reduce y by 14 if it's on the right
              // or add by 14 if it's on the left
              const point2X =
                chartCenterPoint.x + Math.cos(angle) * 90;
              let point2Y =
                chartCenterPoint.y + Math.sin(angle) * 90;
      
              let overlapsedPoint;
              if (point2X < chartCenterPoint.x) {
                overlapsedPoint = leftLabelCoordinates.find(
                  (y) => y + 7 > point2Y && y - 7 < point2Y
                );
              } else {
                overlapsedPoint = rightLabelCoordinates.find(
                  (y) => y + 7 > point2Y && y - 7 < point2Y
                );
              }
      
              if (overlapsedPoint && point2X < chartCenterPoint.x) {
                point2Y += 5;
              } else {
                point2Y -= 5;
              }
      
              let value = dataset.data[i];
              if (dataset.polyline && dataset.polyline.formatter) {
                value = dataset.polyline.formatter(value);
              }
              let edgePointX = point2X < chartCenterPoint.x ? 30 : chart.width - 30;
      
              if (point2X < chartCenterPoint.x) {
                leftLabelCoordinates.push(point2Y);
              } else {
                rightLabelCoordinates.push(point2Y);
              }

              // eslint-disable-next-line eqeqeq
              if (value != 0) {
                // first line
                ctx.strokeStyle = color;
                ctx.beginPath();
                ctx.moveTo(centerPoint.x, centerPoint.y);
                ctx.lineTo(point2X, point2Y);
                ctx.stroke();
                // second line
                ctx.beginPath();
                ctx.moveTo(point2X, point2Y);
                ctx.lineTo(edgePointX, point2Y);
                ctx.stroke();
                // fill custom label
                const labelAlignStyle =
                  edgePointX < chartCenterPoint.x ? "left" : "right";
                const labelX = edgePointX;
                const labelY = point2Y;
                ctx.textAlign = labelAlignStyle;
                ctx.textBaseline = "bottom";
                ctx.fillStyle = labelColor;
                ctx.fillText(value, labelX, labelY);
              }
            });
            ctx.restore();
          }
        },
      ]}
    />
  )
}